import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["container", "sentinel"];

  connect() {
    this.offset = 10;
    this.loading = false;
    this.initialLoadComplete = false;
    
    const projectsCount = parseInt(this.element.dataset.projectsCount || '0', 10);
    this.hasMore = projectsCount > this.offset;
    this.scrollContainer = this.findScrollContainer();
    
    setTimeout(() => {
      this.initialLoadComplete = true;
      this.startObserver();
    }, 1000);
    
    this.element.addEventListener('projects-loaded', () => {
      this.initialLoadComplete = true;
      const projectsCount = parseInt(this.element.dataset.projectsCount || '0', 10);
      this.hasMore = projectsCount > this.offset;
      this.startObserver();
    });
  }

  startObserver() {
    if (this.observer) return;
    if (!this.hasMore || !this.sentinelTarget) return;

    this.scrollContainer = this.findScrollContainer();
    const scrollElement = this.scrollContainer || window;
    
    this.observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry.isIntersecting) return;
        if (!this.initialLoadComplete) return;
        if (this.loading || !this.hasMore) return;
        if (!this.isNearBottom()) return;
        this.loadMore();
      },
      {
        root: this.scrollContainer,
        rootMargin: "0px",
        threshold: 0
      }
    );

    this.observer.observe(this.sentinelTarget);
    
    this.scrollTimeout = null;
    this.scrollHandler = () => {
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      this.scrollTimeout = setTimeout(() => {
        if (this.loading || !this.hasMore || !this.initialLoadComplete) return;
        if (this.isNearBottom()) {
          const rect = this.sentinelTarget.getBoundingClientRect();
          const containerRect = this.scrollContainer?.getBoundingClientRect();
          const isVisible = rect.top < (containerRect?.bottom || window.innerHeight);
          if (isVisible && !this.loading) {
            this.loadMore();
          }
        }
      }, 150);
    };
    
    scrollElement.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  isNearBottom() {
    const scrollElement = this.scrollContainer || document.documentElement;
    const scrollTop = this.scrollContainer ? this.scrollContainer.scrollTop : window.pageYOffset;
    const scrollHeight = this.scrollContainer ? this.scrollContainer.scrollHeight : document.documentElement.scrollHeight;
    const clientHeight = this.scrollContainer ? this.scrollContainer.clientHeight : window.innerHeight;
    const threshold = 100;
    return (scrollTop + clientHeight >= scrollHeight - threshold);
  }

  recreateObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    setTimeout(() => {
      if (this.hasMore && this.sentinelTarget) {
        this.startObserver();
      }
    }, 500);
  }

  findScrollContainer() {
    let element = this.containerTarget;
    
    const kanbanList = element.closest('.kanbanlist');
    if (kanbanList) {
      const style = window.getComputedStyle(kanbanList);
      const overflowY = style.overflowY || style.overflow;
      if ((overflowY === 'auto' || overflowY === 'scroll') && kanbanList.scrollHeight > kanbanList.clientHeight) {
        return kanbanList;
      }
    }
    
    const kanbanProjects = element.closest('.kanban-projects');
    if (kanbanProjects) {
      const style = window.getComputedStyle(kanbanProjects);
      const overflowY = style.overflowY || style.overflow;
      if ((overflowY === 'auto' || overflowY === 'scroll') && kanbanProjects.scrollHeight > kanbanProjects.clientHeight) {
        return kanbanProjects;
      }
    }
    
    element = this.containerTarget.parentElement;
    while (element && element !== document.body) {
      const style = window.getComputedStyle(element);
      const overflowY = style.overflowY || style.overflow;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        if (element.scrollHeight > element.clientHeight) {
          return element;
        }
      }
      element = element.parentElement;
    }
    
    return null;
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
    
    if (this.scrollHandler) {
      const scrollElement = this.scrollContainer || window;
      scrollElement.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
    
    this.removeLoadingFlash();
  }

  loadMore() {
    if (this.loading || !this.hasMore) return;
    
    this.loading = true;
    
    if (this.observer && this.sentinelTarget) {
      try {
        this.observer.unobserve(this.sentinelTarget);
      } catch (e) {
        // Not observing
      }
    }
    
    this.showLoadingFlash();

    const {
      accountId,
      boardId,
      kanbanListId: listId,
      clientId,
      peopleId,
      projectTagId,
      query,
      archived
    } = this.element.dataset;

    const pathSegments = window.location.pathname.split('/').filter(s => s);
    const knownLocales = ['nl', 'fr', 'de', 'zh', 'es', 'ja', 'it', 'pt', 'ko', 'ar'];
    const firstSegment = pathSegments[0];
    const locale = knownLocales.includes(firstSegment) ? firstSegment : '';
    const localePrefix = locale ? `/${locale}` : '';

    const baseUrl = `${localePrefix}/${accountId}/boards/${boardId}/kanban_lists/${listId}/load_projects`;

    const params = new URLSearchParams({
      offset: this.offset,
      ...(peopleId && { people_id: peopleId }),
      ...(query && { q: query }),
      ...(archived === "true" && { archived: "true" })
    });

    this.safeParseArray(clientId).forEach(id =>
      params.append("client_id[]", id)
    );
    this.safeParseArray(projectTagId).forEach(id =>
      params.append("project_tag_id[]", id)
    );

    const url = `${baseUrl}?${params.toString()}`;

    fetch(url, {
      headers: { "X-Requested-With": "XMLHttpRequest" }
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        if (data.html) {
          this.containerTarget.insertAdjacentHTML("beforeend", data.html);
        }
        this.offset += 10;
        this.hasMore = data.more_projects === true;
      })
      .catch(error => {
        console.error('Error loading more projects:', error);
      })
      .finally(() => {
        this.loading = false;
        this.removeLoadingFlash();

        if (this.hasMore && this.sentinelTarget) {
          setTimeout(() => {
            if (!this.hasMore || !this.sentinelTarget) return;
            
            if (!this.observer) {
              this.startObserver();
              return;
            }
            
            try {
              try {
                this.observer.unobserve(this.sentinelTarget);
              } catch (e) {
                // Not observing
              }
              this.observer.observe(this.sentinelTarget);
            } catch (e) {
              this.recreateObserver();
            }
          }, 300);
        } else {
          if (this.sentinelTarget && this.observer) {
            try {
              this.observer.unobserve(this.sentinelTarget);
            } catch (e) {
              // Already unobserved
            }
          }
        }
      });
  }

  showLoadingFlash() {
    if (document.querySelector(".infinite-scroll-flash")) return;

    const flash = document.createElement("div");
    flash.className = "infinite-scroll-flash";
    flash.innerText = "Loading projects… please wait";

    document.body.appendChild(flash);
  }

  removeLoadingFlash() {
    const flash = document.querySelector(".infinite-scroll-flash");
    if (flash) flash.remove();
  }

  safeParseArray(value) {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}
